<template>
  <div class="w-full">
    <div v-if="!state.verdict">
      <slot name="before-form" />
      <InterviewSurveyBuilt
        v-if="state.ready"
        :key="formKey"
        :survey="survey"
        :answers="answers"
        :errors="state.errors"
        :reasons="prefillReasons"
        density="compact"
        @change="handleChange"
      />
      <div class="flex flex-row justify-end items-center gap-x-3">
        <experienceable-pathway-step-button
          :primary="true"
          :loading="state.loading"
          :disabled="submitDisabled"
          :step="step"
          @click="submit"
        >
          Valider
        </experienceable-pathway-step-button>
      </div>
    </div>
    <div v-else>
      <PathwayInlineSurveyVerdict
        v-if="state.verdict.data?.description || state.verdict.data?.score > 0"
        class="bg-white"
        :verdict="state.verdict.data"
      />
      <PathwaySurveyNotification :survey-id="step.steppable.id" :consumer-key="consumerKey" @sent="finishStep" />
      <div class="flex flex-row justify-end items-center gap-x-3">
        <SurveyMSSanteButton
          class="shadow"
          :survey-instance-id="state.instance?.id"
          :consumer-key="consumerKey"
        />
        <slot name="verdict-actions" :loading="state.loading" />
        <BaseButton :loading="state.loading" @click="reset">
          Recommencer le questionnaire
        </BaseButton>
        <BaseButton :primary="true" :loading="state.loading" @click="finishStep">
          Suivant
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive, ref} from "vue";
import ExperienceablePathwayStepButton
    from '@/program/components/experienceable-program-button/ExperienceablePathwayStepButton.vue';
import {Pathway, PathwayStep} from "@/program/interfaces/Program";
import InterviewSurveyBuilt from "@/interview/components/instance-modal/InterviewSurveyBuilt.vue";
import {pathwayApi, surveyApi} from "@/container";
import {PrefilledSurveyAnswer, Survey, SurveyAnswerInstance, SurveyVerdict} from "@/core/interfaces/survey/Survey";
import pathwayService from "@/pathway/services/PathwayService";
import BaseButton from "@/core/components/base/BaseButton.vue";
import PathwayInlineSurveyVerdict from "@/pathway/components/PathwayInlineSurveyVerdict.vue";
import {useToastStore} from "@/stores/toast.store";
import PathwaySurveyNotification from "@/pathway/components/PathwaySurveyNotification.vue";
import SurveyMSSanteButton from "@/mssante/components/SurveyMSSanteButton.vue";
import {SurveyAnswerValues} from "@/pathway/interfaces/PathwaySurvey";

const api = surveyApi();

const props = defineProps<{
    pathway: Pathway;
    step: PathwayStep;
    consumerKey: string;
    survey: Survey;
    /** Ask the API for the answers it can prefill from the consumer data. */
    prefill?: boolean;
    submitDisabled?: boolean;
    /** Ran once the answers are saved, before the step is completed. */
    afterAnswers?: (survey: Survey, answers: SurveyAnswerValues) => Promise<void>;
}>();

const {showError} = useToastStore();

const state = reactive<{
    loading: boolean;
    ready: boolean;
    errors?: Record<number, string | undefined>;
    instance?: SurveyAnswerInstance;
    verdict?: { data: SurveyVerdict };
}>({loading: false, ready: false});

const answerValues = ref<SurveyAnswerValues>({});
const prefilledAnswers = ref<PrefilledSurveyAnswer[]>([]);
const formKey = ref(0);

/**
 * On mounted, prefill the survey before rendering it: the choice components
 * read their initial value when they mount.
 */
onMounted(async () => {
    if (props.prefill) {
        state.loading = true;
        prefilledAnswers.value = await api.prefilledAnswers(props.survey.id, props.consumerKey).catch(() => []);
        seedAnswers();
        state.loading = false;
    }

    state.ready = true;
});

/**
 * Compute the answers array.
 */
const answers = computed(() => {
    return Object.keys(answerValues.value)
        .reduce((acc, key) => {
            acc[key] = answerValues.value[key].length ? answerValues.value[key] : [];

            return acc;
        }, {});
});

/**
 * Compute the prefill explanations, only for the questions actually prefilled.
 */
const prefillReasons = computed<Record<number, string>>(() => {
    return prefilledAnswers.value.reduce((acc, prefilled) => {
        if (prefilled.answer?.length && prefilled.reason) {
            acc[prefilled.question_id] = prefilled.reason;
        }

        return acc;
    }, {} as Record<number, string>);
});

/**
 * Seed the form with the answers the API was able to prefill.
 */
const seedAnswers = () => {
    answerValues.value = prefilledAnswers.value.reduce((acc, prefilled) => {
        const values = (prefilled.answer ?? [])
            .map(answer => typeof answer === 'object' && answer !== null ? answer.id : answer);

        if (values.length) {
            acc[prefilled.question_id] = values;
        }

        return acc;
    }, {} as SurveyAnswerValues);
};

/**
 * Handle the user answers.
 */
const handleChange = ({value, questionId}: { value: unknown, questionId: number }) => {
    state.errors = undefined;
    answerValues.value[questionId] = Array.isArray(value) ? value : [value];
};

/**
 * Submit the survey.
 */
const submit = async () => {
    state.loading = true;

    await uploadAnswers().catch(() => {
        showError({
            content: 'Une erreur est survenue lors de l\'envoi des réponses.'
        });
    });

    state.loading = false;
};

/**
 * Upload the answers to the API.
 */
const uploadAnswers = async () => {
    if (state.errors) {
        return Promise.reject();
    }

    if (!state.instance) {
        state.instance = await api.createInstance(props.survey.id, props.consumerKey);
    }

    const formattedAnswers = Object.keys(answerValues.value).map(key => ({
        question_id: Number(key),
        value: answerValues.value[key]
    }));

    await api.answerAll(props.survey.id, state.instance!.id, formattedAnswers, new Date().toISOString());

    await props.afterAnswers?.(props.survey, answerValues.value);

    await pathwayApi()
        .storeConsumerStep(props.step.id, props.consumerKey, state.instance!.id, 'survey-answer');

    return state.verdict = await api.verdict(props.survey.id, state.instance!.id).catch(() => {
        return {data: true};
    });
};

/**
 * Finish the step and load the pathway.
 */
const finishStep = async () => {
    if (!state.instance) {
        return;
    }

    await pathwayService().updatePathway(props.pathway, props.consumerKey);
};

/**
 * Restart the step.
 */
const reset = () => {
    state.loading = false;
    state.verdict = undefined;
    state.instance = undefined;
    state.errors = undefined;
    answerValues.value = {};

    // The choice components hold the highlighted answers on the built survey
    // itself, so clear them before remounting the form.
    (props.survey.sections ?? []).forEach(section => {
        (section.questions ?? []).forEach(question => {
            question.user_answer = null;
        });
    });

    seedAnswers();
    formKey.value += 1;
};

defineExpose({reset});

</script>
